/**
 * App state machine and wiring (integration layer).
 *
 *   start ──onStart──▶ [XR + geo granted]  requestSession (in the click) ──▶ session 'xr'
 *     │                [XR, geo unknown]   GPS prompt + requestSession in the same click ──▶ session 'xr'
 *     │                                    (if that fails: 'locating' → 'ready-ar' ──start-ar──▶ session 'xr')
 *     │                [no XR, iOS-like]   requestPermission + getUserMedia + geo (all in the
 *     │                                    click) ──▶ session 'camera' (or start-screen error)
 *     │                [no XR, not iOS]    clear error, nothing starts
 *     └──start-sim (debug)──▶ session 'sim' (no camera, drag = heading/pitch)
 *   any session ──onExit / XR session end──▶ full cleanup ──▶ start
 *
 * Android without WebXR (and not iOS-like): the start screen shows an error instead of
 * starting anything. iOS (no WebXR there at all): camera mode is the only non-debug path.
 * If the compass fails to start, the AR overlay keeps running (aligned by the manual yaw
 * trim only) with a HUD warning; in camera mode a failed motion-sensor permission aborts
 * the start (iOS onboarding is one tap, one shot: there is no manual yaw trim UI there).
 *
 * One Session object owns everything that lives only while rendering (renderer,
 * ZoneScene, PoseEngine, orientation provider, camera video/stream, listeners). The
 * LakeManager, MapView, position provider and UI live for the whole page.
 */
import * as THREE from 'three';
import type {
  HudState,
  OrientationProvider,
  PositionProvider,
  RenderMode,
  XrViewerSample,
  ZoneStatus,
  LakeENU,
  LakeFile,
} from '../contracts';
import { LAKE_MESSAGES, LakeManager, declinationDeg as autoDeclinationDeg, queryZone } from '../geo';
import { createMapView, type MapViewExt } from '../map';
import { PoseEngine } from '../pose';
import { ZoneScene, type ZoneSceneInput } from '../render';
import { GeolocationProvider, MockPositionProvider, readLastFixCache } from '../sensors/gps';
import { MockOrientationProvider, createOrientationProvider } from '../sensors/orientation';
import { CAMERA_LONG_SIDE_FOV_DEG, debugStore, settingsStore } from '../settings';
import { createUi, type UiController } from '../ui';
import { buildDebugRows, hudZone, overlayAlpha, pickMessage, type PositionErrorState } from './hud-state';
import {
  WakeLock,
  exitFullscreen,
  isArSupported,
  isIosLike,
  openRearCamera,
  queryGeoPermission,
  stopStream,
  type GeoPermission,
} from './platform';
import './app.css';

const AR_UNSUPPORTED_MESSAGE = 'Dieses Gerät unterstützt kein AR (Google Play-Dienste für AR / ARCore erforderlich).';
const CAMERA_DENIED_MESSAGE = 'Kamera-Zugriff verweigert. Bitte in Einstellungen › Safari erlauben.';
const CAMERA_UNAVAILABLE_MESSAGE = 'Kamera nicht verfügbar. Bitte in Einstellungen › Safari erlauben.';
const MOTION_DENIED_MESSAGE = 'Bewegungssensor-Zugriff verweigert. Bitte in Einstellungen › Safari erlauben.';

/** Zone query rate limit (ms): 10 Hz. */
const ZONE_QUERY_MS = 100;
/** HUD text-ish side outputs (map user, lake manager, debug rows): 2 Hz. */
const SLOW_MS = 500;
/** Delay before the (blocking) zone triangulation so the "Lade Seedaten…" chip gets painted. */
const LAKE_BUILD_DELAY_MS = 80;
/** Sim drag: degrees of heading per viewport width. */
const SIM_DRAG_H_DEG = 60;

type AppPhase = 'start' | 'locating' | 'ready-ar' | 'starting' | 'session';

interface Session {
  mode: RenderMode;
  canvas: HTMLCanvasElement;
  renderer: THREE.WebGLRenderer;
  scene: ZoneScene;
  engine: PoseEngine;
  xr: XRSession | null;
  /** mode 'camera': the rear-camera <video> and its MediaStream. */
  video: HTMLVideoElement | null;
  stream: MediaStream | null;
  orientation: OrientationProvider | null;
  offOrientation: (() => void) | null;
  offPosition: (() => void) | null;
  noCompass: boolean;
  /** For the debug overlay (B.6). */
  orientationStatus: string;
  orientationError: string | null;
  cleanups: (() => void)[];
  ended: boolean;
  // Lake meshes
  sceneLake: LakeENU | null;
  pendingLake: LakeENU | null | undefined;
  lakeTimer: number;
  // Per-frame state (reused objects)
  readonly xrSample: XrViewerSample;
  readonly input: ZoneSceneInput;
  readonly hud: HudState;
  zone: ZoneStatus | null;
  zoneLake: LakeENU | null;
  lastZoneQuery: number;
  lastSlow: number;
  frames: number;
  fps: number;
  fpsSince: number;
  mapRotation: number;
}

export class App {
  readonly ui: UiController;
  readonly map: MapViewExt;
  readonly lakes = new LakeManager();
  private readonly root: HTMLElement;
  private readonly wakeLock = new WakeLock();
  private readonly autoDecl = autoDeclinationDeg();

  private phase: AppPhase = 'start';
  private xrSupported: boolean | null = null;
  private readonly iosLike = isIosLike();
  private geoPermission: GeoPermission = 'unknown';
  private session: Session | null = null;

  private position: PositionProvider | null = null;
  private positionIsMock = false;
  private positionOffs: (() => void)[] = [];
  private positionError: PositionErrorState = null;
  private mapFiles: LakeFile[] | null = null;
  private mapOpen = false;

  constructor(root: HTMLElement) {
    this.root = root;
    this.ui = createUi(root, {
      onStart: () => this.onStart(),
      onExit: () => this.exit(),
      onAction: (id) => this.onAction(id),
      onMockNudge: (e, n) => this.onMockNudge(e, n),
      onOpenMap: () => this.openMap(),
    });

    this.map = createMapView();
    this.ui.mapSlots.mini.append(this.map.element);
    this.map.setMode('hidden');
    this.map.onRequestFull(() => this.openMap());
    this.map.onClose(() => this.closeMap());
    // Last-known fix (localStorage, display only): centres the minimap immediately, before any
    // live fix exists. Never feeds the Kalman filter / gate / zone queries / AR overlay.
    const cached = readLastFixCache(typeof localStorage !== 'undefined' ? localStorage : undefined);
    if (cached) this.map.setCachedUser({ lat: cached.lat, lon: cached.lon }, cached.accuracy);

    this.lakes.onChange(() => this.onLakesChanged());
    settingsStore.subscribe(() => this.onSettingsChanged());
    debugStore.subscribe(() => this.onDebugChanged());
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);

    this.ui.showStart();
    this.ui.setStartPhase('idle');
    void this.probe();
    void this.lakes.loadAll().then(() => this.onLakesChanged());
  }

  // ─── Start screen ─────────────────────────────────────────────────────────

  private async probe(): Promise<void> {
    const [xr, geo] = await Promise.all([
      isArSupported(),
      queryGeoPermission((s) => {
        this.geoPermission = s;
        if (s === 'granted' && this.phase === 'locating') this.setPhase('ready-ar');
        if (s === 'granted') this.startEarlyPosition();
      }),
    ]);
    this.xrSupported = xr;
    this.geoPermission = geo;
    document.documentElement.dataset.xr = String(xr);
    if (!xr && !this.iosLike && this.phase === 'start') {
      this.ui.setStartPhase('error', AR_UNSUPPORTED_MESSAGE);
    }
    // Geolocation permission was already 'granted' at page load (not just granted just now via the
    // change listener above): start the watch immediately so the fix converges while the user is
    // still on the start screen. "App starten" then reuses this running provider (ensurePosition()).
    if (geo === 'granted') this.startEarlyPosition();
  }

  /** Starts the position provider ahead of "App starten", if not already running. Same provider,
   * same Kalman/gate as after start; ensurePosition() dedupes so the later "App starten" tap and
   * startXr()/startSim() just reuse it (no second watch, no second permission prompt). */
  private startEarlyPosition(): void {
    if (this.phase !== 'start') return;
    this.ensurePosition().start().catch(() => undefined);
  }

  private setPhase(p: AppPhase, message?: string): void {
    this.phase = p;
    if (p === 'locating' || p === 'ready-ar' || p === 'starting') this.ui.setStartPhase(p, message);
  }

  /** "App starten" click. Everything that needs the user activation happens synchronously here. */
  private onStart(): void {
    if (this.phase !== 'start') return;
    const xrPossible = this.xrSupported ?? !!navigator.xr;
    if (!xrPossible) {
      if (this.iosLike) this.startCamera();
      else this.ui.setStartPhase('error', AR_UNSUPPORTED_MESSAGE);
      return;
    }
    const geoReady = this.geoPermission === 'granted' || debugStore.get().mockGps;
    if (geoReady) {
      this.startXr(false);
      return;
    }
    // One tap for everything: trigger the location prompt and request the AR session in the same
    // click. Chrome queues the permission dialogs (location, then camera/AR) one after the other.
    // requestSession only needs the transient activation at call time, so both calls stay inside it.
    // If the session request fails (e.g. dialogs collided), fall back to the two-step flow.
    this.ensurePosition().start().catch(() => undefined);
    this.startXr(true);
  }

  /** Two-step fallback: wait for the location permission, then offer "AR-Kamera starten". */
  private startLocatingFallback(): void {
    this.setPhase('locating');
    const pos = this.ensurePosition();
    pos.start().then(
      () => {
        if (this.phase === 'locating') this.setPhase('ready-ar');
      },
      () => {
        if (this.phase !== 'locating') return;
        this.phase = 'start';
        this.stopPosition();
        this.ui.setStartPhase('error', 'Standortzugriff verweigert. Bitte in den Browser-Einstellungen erlauben.');
      },
    );
  }

  private onAction(id: string): void {
    if (id === 'start-ar') {
      if (this.phase === 'ready-ar') this.startXr(false);
    } else if (id === 'start-sim') {
      this.startSim();
    }
  }

  // ─── Modes ────────────────────────────────────────────────────────────────

  /** Mode A (only mode besides debug 'sim'). Must run inside the click (requestSession before any await). */
  private startXr(fallbackOnFailure: boolean): void {
    const xr = navigator.xr;
    if (!xr) {
      this.ui.setStartPhase('error', AR_UNSUPPORTED_MESSAGE);
      return;
    }
    const s = this.createSession('xr');
    let request: Promise<XRSession>;
    try {
      request = xr.requestSession('immersive-ar', {
        requiredFeatures: ['local'],
        optionalFeatures: ['dom-overlay'],
        domOverlay: { root: this.ui.overlayRoot },
      });
    } catch (e) {
      request = Promise.reject(e);
    }
    // B.3: start the orientation provider synchronously, right after requestSession, so any
    // Generic Sensor / deviceorientation permission prompt still has this click's user activation.
    this.startOrientation(s, false);
    this.setPhase('starting');
    this.wakeLock.request();
    const pos = this.ensurePosition();
    pos.start().catch(() => undefined);
    request.then(
      (xrSession) => this.beginXr(s, xrSession),
      (err: unknown) => {
        console.warn('[app] requestSession failed:', err);
        this.teardown(s, false);
        if (fallbackOnFailure && this.geoPermission !== 'granted') {
          this.startLocatingFallback();
          return;
        }
        this.ui.toast('AR-Start fehlgeschlagen', 'error');
      },
    );
  }

  private async beginXr(s: Session, xrSession: XRSession): Promise<void> {
    s.xr = xrSession;
    const r = s.renderer;
    r.xr.enabled = true;
    r.xr.setReferenceSpaceType('local');
    xrSession.addEventListener('end', () => this.onXrEnd(s));
    try {
      await r.xr.setSession(xrSession);
    } catch (err) {
      console.error('[app] setSession failed:', err);
      this.ui.toast('AR-Start fehlgeschlagen', 'error');
      xrSession.end().catch(() => undefined);
      return;
    }
    const ref = r.xr.getReferenceSpace();
    if (ref) {
      const onReset = () => s.engine.notifyXrReset();
      ref.addEventListener('reset', onReset);
      s.cleanups.push(() => ref.removeEventListener('reset', onReset));
    }
    // Retry once if the synchronous attempt (before the session existed) failed: the
    // permission prompt triggered by that first attempt may have resolved by now.
    if (s.noCompass) this.startOrientation(s, false);
    this.activate(s);
    r.setAnimationLoop((t, frame) => this.frame(s, t, frame));
  }

  /**
   * Mode C (iOS, only non-debug path when WebXR isn't supported). Everything that needs
   * the click's user activation runs synchronously here, in this order: the motion-sensor
   * permission prompt (inside startOrientation -> createOrientationProvider ->
   * IosOrientationProvider.start(), so the very first statement is
   * DeviceOrientationEvent.requestPermission()), then getUserMedia, then the geolocation
   * watch. The session becomes visible once compass and camera run (GPS is not awaited;
   * the HUD handles waiting/denied like in XR). A denied compass or camera goes back to the
   * start screen with a German error (no manual yaw trim on iOS to fall back on).
   */
  private startCamera(): void {
    const s = this.createSession('camera');
    const orientation = this.startOrientation(s, false);
    const video = document.createElement('video');
    video.className = 'uz-app-video';
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('autoplay', '');
    this.root.insertBefore(video, s.canvas);
    s.video = video;
    const media = openRearCamera(video);
    this.wakeLock.request();
    const pos = this.ensurePosition();
    const posStart = pos.start();
    this.setPhase('starting', 'Kamera wird gestartet …');

    const onVisible = () => {
      if (document.visibilityState === 'visible') video.play().catch(() => undefined);
    };
    document.addEventListener('visibilitychange', onVisible);
    s.cleanups.push(() => document.removeEventListener('visibilitychange', onVisible));
    const onVideoResize = () => this.resize(s);
    video.addEventListener('loadedmetadata', onVideoResize);
    video.addEventListener('resize', onVideoResize);
    s.cleanups.push(() => {
      video.removeEventListener('loadedmetadata', onVideoResize);
      video.removeEventListener('resize', onVideoResize);
    });

    // GPS is not awaited (a cold fix can take many seconds): like XR, the session starts as soon as
    // camera and compass run; the HUD shows "Warte auf GPS" / the location-denied message meanwhile.
    posStart.catch(() => undefined);
    Promise.allSettled([orientation.start(), media]).then(([oriRes, mediaRes]) => {
      if (s.ended) {
        if (mediaRes.status === 'fulfilled') stopStream(mediaRes.value);
        return;
      }
      if (oriRes.status === 'rejected') {
        this.teardown(s, false);
        this.ui.setStartPhase('error', MOTION_DENIED_MESSAGE);
        return;
      }
      if (mediaRes.status === 'rejected') {
        this.teardown(s, false);
        const code = (mediaRes.reason as { name?: string } | undefined)?.name;
        this.ui.setStartPhase('error', code === 'NotAllowedError' ? CAMERA_DENIED_MESSAGE : CAMERA_UNAVAILABLE_MESSAGE);
        return;
      }
      s.stream = mediaRes.value;
      this.activate(s);
      s.renderer.setAnimationLoop((t) => this.frame(s, t));
    });
  }

  /** Mode S (debug): no camera, sky/water background, mock orientation driven by dragging. */
  private startSim(): void {
    this.ui.closeSettings();
    if (this.session) this.teardown(this.session, true);
    this.wakeLock.request();
    const s = this.createSession('sim');
    this.ensurePosition().start().catch(() => undefined);
    const mock = this.startOrientation(s, true) as MockOrientationProvider;
    this.attachSimDrag(s, mock);
    this.activate(s);
    s.renderer.setAnimationLoop((t) => this.frame(s, t));
  }

  private onXrEnd(s: Session): void {
    this.teardown(s, false);
  }

  private exit(): void {
    const s = this.session;
    if (!s) return;
    if (s.xr) {
      // The 'end' event does the cleanup.
      s.xr.end().catch(() => this.teardown(s, false));
    } else {
      this.teardown(s, false);
    }
  }

  // ─── Session lifecycle ────────────────────────────────────────────────────

  private createSession(mode: RenderMode): Session {
    const canvas = document.createElement('canvas');
    canvas.className = 'uz-app-canvas';
    // In XR the canvas is never shown (WebXR composites); in the dom-overlay it would cover the camera.
    canvas.hidden = mode === 'xr';
    this.root.insertBefore(canvas, this.ui.overlayRoot);
    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x000000, 0);
    const scene = new ZoneScene(renderer);
    // Sim: the virtual device is always held the way the viewport is oriented (no physical screen
    // rotation to compensate), so the screen angle is 0; the viewport aspect still sets the FOV.
    // Camera: fixed device FOV (no debug slider) along the long side; screen angle follows the
    // real (or window.orientation-fallback) screen rotation, same default as sim/xr use.
    const engine = new PoseEngine(
      mode === 'sim'
        ? { mode, getScreenAngleDeg: () => 0 }
        : mode === 'camera'
          ? { mode, getVFovBaseDeg: () => CAMERA_LONG_SIDE_FOV_DEG }
          : { mode },
    );
    engine.setAutoDeclinationDeg(this.autoDecl);
    const now = performance.now();
    const s: Session = {
      mode,
      canvas,
      renderer,
      scene,
      engine,
      xr: null,
      video: null,
      stream: null,
      orientation: null,
      offOrientation: null,
      offPosition: null,
      noCompass: false,
      orientationStatus: 'idle',
      orientationError: null,
      cleanups: [],
      ended: false,
      sceneLake: null,
      pendingLake: undefined,
      lakeTimer: 0,
      xrSample: { position: [0, 0, 0], orientation: [0, 0, 0, 1], emulatedPosition: false, time: 0 },
      input: {
        mode,
        alignment: null,
        camera: null,
        userENU: null,
        headingDeg: null,
        time: 0,
        eyeHeightM: 1.2,
        overlayAlpha: 0,
        overlayOpacity: 1,
        viewDistanceM: settingsStore.get().viewDistanceM,
        debugHelpers: false,
      },
      hud: {
        mode,
        headingDeg: null,
        gpsAccuracy: null,
        gpsGate: 'waiting',
        gpsThresholdM: settingsStore.get().gpsThresholdM,
        tracking: 'lost',
        zone: null,
        speedKmh: null,
        hudRotationDeg: 0,
        lakeMessage: null,
        message: null,
        debugRows: null,
      },
      zone: null,
      zoneLake: null,
      lastZoneQuery: -Infinity,
      lastSlow: -Infinity,
      frames: 0,
      fps: 0,
      fpsSince: now,
      mapRotation: 0,
    };
    return s;
  }

  /** Make `s` the running session: attach GPS, lake, viewport, HUD. */
  private activate(s: Session): void {
    this.session = s;
    this.phase = 'session';
    const pos = this.ensurePosition();
    s.offPosition = s.engine.attach(pos, null);
    this.applyLake(s);
    this.resize(s);
    this.ui.showSession();
    this.updateMapMode();
    document.documentElement.dataset.mode = s.mode;
  }

  private startOrientation(s: Session, forceMock: boolean): OrientationProvider {
    s.offOrientation?.();
    s.orientation?.stop();
    const getDeclinationDeg = () => this.declination();
    const o = forceMock
      ? new MockOrientationProvider({ getDeclinationDeg })
      : createOrientationProvider({ mockOptions: { getDeclinationDeg } });
    s.orientation = o;
    s.noCompass = false;
    s.orientationStatus = 'startet …';
    s.orientationError = null;
    s.engine.setOrientationFailed(false);
    s.offOrientation = s.engine.attach(null, o);
    // B.6: keep the last provider error around for the debug overlay.
    s.cleanups.push(
      o.onError((err) => {
        if (s.orientation === o) s.orientationError = err.message;
      }),
    );
    o.start().then(
      () => {
        if (s.orientation === o) s.orientationStatus = 'ok';
      },
      (err: unknown) => {
        console.warn('[app] orientation failed:', err);
        if (s.orientation === o) {
          s.noCompass = true;
          s.orientationStatus = 'fehlgeschlagen';
          s.orientationError = err instanceof Error ? err.message : String(err);
          s.engine.setOrientationFailed(true);
        }
      },
    );
    return o;
  }

  /** Stop and release everything of `s`. `keepRunning`: another session follows immediately. */
  private teardown(s: Session, keepRunning: boolean): void {
    if (s.ended) return;
    s.ended = true;
    if (this.session === s) this.session = null;
    window.clearTimeout(s.lakeTimer);
    s.renderer.setAnimationLoop(null);
    s.cleanups.forEach((c) => c());
    s.offOrientation?.();
    s.orientation?.stop();
    s.offPosition?.();
    stopStream(s.stream);
    if (s.video) {
      s.video.srcObject = null;
      s.video.remove();
    }
    s.scene.dispose();
    s.renderer.dispose();
    s.renderer.forceContextLoss();
    s.canvas.remove();
    if (keepRunning) return;

    this.wakeLock.release();
    this.stopPosition();
    exitFullscreen();
    this.mapOpen = false;
    void this.ui.setMapOpen(false);
    this.ui.mapSlots.mini.append(this.map.element);
    this.ui.showStart();
    this.ui.setStartPhase('idle');
    this.phase = 'start';
    this.updateMapMode();
    delete document.documentElement.dataset.mode;
  }

  // ─── Position provider (page lifetime while started) ─────────────────────

  private ensurePosition(): PositionProvider {
    const wantMock = debugStore.get().mockGps;
    if (this.position && this.positionIsMock === wantMock) return this.position;
    const wasRunning = this.position !== null;
    this.stopPosition();
    const p: PositionProvider = wantMock
      ? new MockPositionProvider({ getHeadingDeg: () => this.session?.engine.state.headingDeg ?? null })
      : new GeolocationProvider();
    this.position = p;
    this.positionIsMock = wantMock;
    this.positionError = null;
    this.positionOffs = [
      p.onError((e) => {
        this.positionError = e.code === 'denied' ? 'denied' : e.code === 'unavailable' ? 'unavailable' : this.positionError;
      }),
      p.onFix(() => {
        this.positionError = null;
      }),
    ];
    const s = this.session;
    if (s) {
      s.offPosition?.();
      s.offPosition = s.engine.attach(p, null);
    }
    if (wasRunning || s) p.start().catch(() => undefined);
    return p;
  }

  private stopPosition(): void {
    this.positionOffs.forEach((off) => off());
    this.positionOffs = [];
    this.position?.stop();
    this.position = null;
    this.positionError = null;
  }

  // ─── Per frame ────────────────────────────────────────────────────────────

  private frame(s: Session, t: number, xrFrame?: XRFrame): void {
    if (s.ended) return;
    const now = performance.now();
    const set = settingsStore.get();
    const dbg = debugStore.get();

    // XR viewer pose (null = tracking loss).
    let xr: XrViewerSample | null = null;
    if (s.mode === 'xr' && xrFrame) {
      const ref = s.renderer.xr.getReferenceSpace();
      const pose = ref ? xrFrame.getViewerPose(ref) : undefined;
      if (pose) {
        const p = pose.transform.position;
        const q = pose.transform.orientation;
        const xs = s.xrSample;
        xs.position[0] = p.x;
        xs.position[1] = p.y;
        xs.position[2] = p.z;
        xs.orientation[0] = q.x;
        xs.orientation[1] = q.y;
        xs.orientation[2] = q.z;
        xs.orientation[3] = q.w;
        xs.emulatedPosition = pose.emulatedPosition;
        xs.time = t;
        xr = xs;
      }
    }

    const st = s.engine.update(now, xr);

    // Slow path (2 Hz): lake selection, map user marker, fps.
    const slow = now - s.lastSlow >= SLOW_MS;
    if (slow) {
      s.lastSlow = now;
      if (st.userLatLon) this.lakes.update(st.userLatLon);
      this.map.setUser(st.userLatLon, st.headingDeg, st.gpsAccuracy);
      s.fps = (s.frames * 1000) / Math.max(1, now - s.fpsSince);
      s.frames = 0;
      s.fpsSince = now;
    }
    s.frames++;

    // Zone status (10 Hz).
    const lake = this.lakes.current;
    if (now - s.lastZoneQuery >= ZONE_QUERY_MS || lake !== s.zoneLake) {
      s.lastZoneQuery = now;
      s.zoneLake = lake;
      s.zone =
        lake && st.userENU && st.userLatLon && s.engine.getFrame() === lake.frame
          ? hudZone(queryZone(lake, st.userENU, st.userLatLon))
          : null;
    }
    const lakeBuilding = s.pendingLake !== undefined && s.pendingLake !== null;

    // Scene.
    const inp = s.input;
    inp.mode = st.mode;
    inp.alignment = st.alignment;
    inp.camera = st.camera;
    inp.userENU = st.userENU;
    inp.headingDeg = st.headingDeg;
    inp.time = now;
    inp.eyeHeightM = set.eyeHeightM;
    inp.overlayAlpha = overlayAlpha(st.gpsGate, st.tracking);
    inp.overlayOpacity = set.overlayOpacity;
    inp.viewDistanceM = set.viewDistanceM;
    inp.debugHelpers = dbg.enabled && dbg.showHorizon;
    s.scene.apply(inp);
    s.scene.render();

    // HUD.
    const hud = s.hud;
    const zone = st.gpsGate === 'waiting' || lakeBuilding ? null : s.zone;
    hud.mode = st.mode;
    hud.headingDeg = st.headingDeg;
    hud.gpsAccuracy = st.gpsAccuracy;
    hud.gpsGate = st.gpsGate;
    hud.gpsThresholdM = set.gpsThresholdM;
    hud.tracking = st.tracking;
    hud.zone = zone;
    hud.speedKmh = st.speedKmh;
    hud.hudRotationDeg = s.engine.hudRotationDeg;
    hud.lakeMessage = lakeBuilding ? LAKE_MESSAGES.loading : this.lakes.message;
    hud.message = pickMessage({
      mode: st.mode,
      gpsGate: st.gpsGate,
      tracking: st.tracking,
      noCompass: s.noCompass,
      positionError: this.positionError,
      zone,
    });
    if (dbg.enabled && dbg.showRaw) {
      if (slow || !hud.debugRows) {
        hud.debugRows = buildDebugRows(st, s.engine.diagnostics, {
          lakeId: lake?.meta.id ?? null,
          fps: s.fps,
          drawnTriangles: s.scene.getStats().drawnTriangles,
          positionSource: this.positionIsMock ? 'mock' : 'gps',
          hudRotationDeg: this.ui.hudRotationDeg,
          orientationStatus: s.orientationStatus,
          orientationError: s.orientationError,
        });
      }
    } else {
      hud.debugRows = null;
    }
    this.ui.updateHud(hud);

    // Map pointer input follows the HUD counter-rotation.
    const rot = this.ui.hudRotationDeg;
    if (rot !== s.mapRotation) {
      s.mapRotation = rot;
      this.map.setInputRotation(rot);
    }
  }

  // ─── Lakes ────────────────────────────────────────────────────────────────

  private onLakesChanged(): void {
    if (this.lakes.files !== this.mapFiles && this.lakes.files.length > 0) {
      this.mapFiles = this.lakes.files;
      this.map.setLakes(this.lakes.files);
    }
    const s = this.session;
    if (s) this.applyLake(s);
  }

  /** New current lake: switch the pose frame now, build the meshes a moment later (blocking ~0.5 s). */
  private applyLake(s: Session): void {
    const lake = this.lakes.current;
    if (s.engine.getFrame() !== (lake?.frame ?? null)) s.engine.setFrame(lake?.frame ?? null);
    const target = s.pendingLake !== undefined ? s.pendingLake : s.sceneLake;
    if (lake === target) return;
    s.pendingLake = lake;
    window.clearTimeout(s.lakeTimer);
    s.lakeTimer = window.setTimeout(() => {
      if (s.ended || s.pendingLake === undefined) return;
      const next = s.pendingLake;
      s.scene.setLake(next);
      s.sceneLake = next;
      s.pendingLake = undefined;
    }, lake ? LAKE_BUILD_DELAY_MS : 0);
  }

  // ─── Map ──────────────────────────────────────────────────────────────────

  private openMap(): void {
    if (!this.session || this.mapOpen) return;
    this.mapOpen = true;
    this.ui.mapSlots.full.append(this.map.element);
    this.map.setMode('full');
    this.ui.setMapOpen(true);
  }

  private closeMap(): void {
    if (!this.mapOpen) return;
    this.mapOpen = false;
    // Keep the map in the full layer while it shrinks, then move it back into the minimap.
    void this.ui.setMapOpen(false).then(() => {
      if (this.mapOpen) return;
      this.ui.mapSlots.mini.append(this.map.element);
      this.updateMapMode();
    });
  }

  private updateMapMode(): void {
    if (this.mapOpen) return;
    const show = this.session !== null && settingsStore.get().showMinimap;
    this.map.setMode(show ? 'mini' : 'hidden');
  }

  // ─── Callbacks / settings ─────────────────────────────────────────────────

  private declination(): number {
    const s = settingsStore.get();
    return s.declinationMode === 'auto' ? this.autoDecl : s.declinationDeg;
  }

  private onMockNudge(dEast: number, dNorth: number): void {
    if (this.position instanceof MockPositionProvider) {
      this.position.nudge(dEast, dNorth);
    } else {
      // Not running (start screen) or real GPS: move the stored mock position.
      const d = debugStore.get();
      const mLat = 111_132;
      const mLon = 111_320 * Math.cos((d.mockLat * Math.PI) / 180);
      debugStore.update({ mockLat: d.mockLat + dNorth / mLat, mockLon: d.mockLon + dEast / mLon });
      if (!d.mockGps) this.ui.toast('Mock-GPS ist aus', 'info');
    }
  }

  private onSettingsChanged(): void {
    this.updateMapMode();
  }

  private onDebugChanged(): void {
    // Swap GPS <-> mock live while a provider is in use.
    if (this.position && this.positionIsMock !== debugStore.get().mockGps) this.ensurePosition();
    // Swap compass <-> mock compass live (compass / xr modes; sim always uses the mock).
    const s = this.session;
    if (s && s.mode !== 'sim') {
      const isMock = s.orientation instanceof MockOrientationProvider;
      if (isMock !== debugStore.get().mockHeading) this.startOrientation(s, false);
    }
  }

  // ─── Viewport / sim drag ──────────────────────────────────────────────────

  private readonly onResize = () => {
    if (this.session) this.resize(this.session);
  };

  private resize(s: Session): void {
    if (s.mode === 'xr') return; // WebXR owns the size; the orientation is locked.
    const w = window.innerWidth;
    const h = window.innerHeight;
    s.renderer.setSize(w, h, false);
    s.scene.resize(w, h);
    s.engine.setViewport(w, h);
  }

  /** Drag on the free HUD area: horizontal = heading, vertical = pitch (grab-the-world feel). */
  private attachSimDrag(s: Session, mock: MockOrientationProvider): void {
    const root = this.ui.overlayRoot;
    let id: number | null = null;
    let lastX = 0;
    let lastY = 0;
    const down = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (id !== null || target !== s.canvas) return;
      id = e.pointerId;
      lastX = e.clientX;
      lastY = e.clientY;
    };
    const move = (e: PointerEvent) => {
      if (e.pointerId !== id) return;
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      const vFov = s.engine.state.camera?.vFovDeg ?? 60;
      mock.addHeadingDeg((-(e.clientX - lastX) / w) * SIM_DRAG_H_DEG);
      mock.setPitchDeg(mock.pitchDeg + ((e.clientY - lastY) / h) * vFov);
      lastX = e.clientX;
      lastY = e.clientY;
    };
    const up = (e: PointerEvent) => {
      if (e.pointerId === id) id = null;
    };
    root.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    s.cleanups.push(() => {
      root.removeEventListener('pointerdown', down);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    });
  }
}
